<template>
    <div>
        <b-modal id="modal-data-check" size="lg" :title="'Data Check Confirmation for '+brand.name+' in '+country.name" hide-backdrop @ok="onOk" @cancel="onCancel">
            <form ref="dataCheckForm" action="/data-check/store" method="POST">
                <input type="hidden" name="_token" :value="csrf">
                <input type="hidden" name="user_id" :value="user.id">
                <input type="hidden" name="country_id" :value="country.id">
                <input type="hidden" name="brand_id" :value="brand.id">
                <input type="hidden" name="notes" :value="researchNotes">
                <div class="row mt-2 mb-2 mx-1">
                    <div class="col-12 text-info" v-if="showHelpTextsInline"><p>{{ helpTexts.sourceUrls }}</p></div>
                    <div class="col-md-9">
                        <b-form-group>
                            <label for="checkbox-urls" class="font-weight-bolder border-bottom w-100">
                                Source URLs
                                <span class="fa fa-info-circle px-2 text-warning"
                                      v-b-popover.hover.righttop="helpTexts.sourceUrls"
                                      title="Source URLs"
                                ></span>
                                <span class="text-warning font-weight-normal">(Required)</span>
                            </label>
                            <b-form-checkbox-group
                                id="checkbox-urls"
                                v-model="selectedUrls"
                                :options="urls"
                                name="checkbox-urls"
                                stacked
                            ></b-form-checkbox-group>
                        </b-form-group>
                    </div>
                    <div class="col-md-3">
                        <b-button v-b-toggle.add-url class="btn btn-sm float-right" variant="primary">
                            <span class="fa fa-plus dropdown-menu-icon" aria-hidden="true" >&nbsp;</span>
                            Add source URL
                        </b-button>
                        <b-button v-if="selectedUrls.length > 0" class="btn btn-sm mt-1 float-right" variant="outline-danger" @click="onArchiveUrl()">
                            <span class="fa fa-archive dropdown-menu-icon" aria-hidden="true" >&nbsp;</span>
                            Archive selected
                        </b-button>
                    </div>
                    <div class="col-12">
                        <b-collapse id="add-url" class="mt-1">
                            <b-input-group class="mb-3">
                                <b-form-input type="url" v-model="url" size="sm" placeholder="https://"
                                              :class="urlIsInvalid ? 'cax-bg-danger' : ''"></b-form-input>
                                <b-input-group-append>
                                    <b-button variant="primary" size="sm" @click="storeUrl()" :disabled="addUrlIsDisabled">Add</b-button>
                                </b-input-group-append>
                            </b-input-group>
                        </b-collapse>
                    </div>
                </div>
                <!--Source Data Files -->
                <div class="row mt-3 mb-2 mx-1">
                    <div class="col-12 text-info" v-if="showHelpTextsInline"><p>{{ helpTexts.sourceDataFiles }}</p></div>
                    <div class="col-md-9 border-bottom">
                        <label for="file-list" class="font-weight-bolder">
                            Source Data Files
                            <span class="fa fa-info-circle px-2"
                                  v-b-popover.hover.righttop="helpTexts.sourceDataFiles"
                                  title="Source Data Files"
                            ></span>
                        </label>
                    </div>
                    <div class="col-md-3">
                        <b-button v-b-toggle.upload-file
                                  @click="$refs.fileInput.click()"
                                  class="btn btn-sm float-right"
                                  :disabled="addSourceFileButtonIsDisabled"
                                  variant="primary"
                        >
                            <span class="fa fa-plus dropdown-menu-icon" aria-hidden="true" >&nbsp;</span>
                            Add source file
                        </b-button>
                    </div>
                </div>
                <div class="row mt-1 mx-1">
                    <div v-if="uploadedFiles" class="col-12">
                        <table v-if="uploadedFiles.length" class="w-100 mx-2" id="file-list">
                            <thead>
                            <tr>
                                <th colspan="2">File name</th>
                                <th>Type</th>
                                <th colspan="2">Date</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="uploadedFile in uploadedFiles" >
                                <td v-b-popover.hover.righttop="filePopOverText(uploadedFile)" title="File Details">
                                    <span :class="fileFaIcon(uploadedFile.media[0].mime_type)">&nbsp;</span>
                                </td>
                                <td>
                                    {{ formatFileNameText(uploadedFile.name) }}
                                </td>
                                <td>
                                    {{ uploadedFile.type }}
<!--                                    {{ uploadedFile.media[0].mime_type }}-->
                                </td>
                                <td>
                                    {{ uploadedFile.data_date || shortDate(uploadedFile.created_at) }}
                                    <span v-if="uploadedFile.data_date"
                                          class="fa fa-calendar-alt ml-1"
                                          v-b-popover.hover.righttop="'Created: '+shortDate(uploadedFile.created_at)"
                                    ></span>
                                </td>
                                <td>
                                    <b-button size="sm" variant="outline-danger border-0"
                                              @click="archiveUploadFile(uploadedFile.id)"
                                              v-b-popover.hover.righttop="'Archive this file'"
                                    >
                                        <span class="fa fa-archive"></span>
                                    </b-button>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                        <div class="col-12 mt-3 p-0">
                            <input id="file" style="display: none;" type="file" ref="fileInput" @change="onFileSelected">
                            <b-card
                                v-if="uploadFormIsEnabled"
                                class="mt-1 mb-2"
                                border-variant="secondary"
                                header="Upload data source file"
                                header-border-variant="secondary"
                                align="left"
                                @cancel="onFileCancel"
                            >
                                <b-card-text class="left">
                                    <div class="mb-2">
                                        <span class="fa-2x" :class="fileFaIcon(selectedFile.type)"></span>
                                        <span>
                                            {{ selectedFile ? selectedFile.name : '' }} ({{ selectedFile ? humanFileSize(selectedFile.size) : ''}})
                                        </span>
                                    </div>
                                    <div v-if="!fileFaIcon(selectedFile.type)" class="alert-danger rounded border-warning my-2 p-2">
                                        The selected file's type {{ selectedFile.type }} is not supported for uploading here!
                                    </div>
                                    <div v-if="selectedFile.size > maxUploadSize" class="alert-danger rounded border-warning my-2 p-2">
                                        The selected file's size, {{ humanFileSize(selectedFile.size) }}, exceeds the maximum allowed
                                        size of {{ humanFileSize(maxUploadSize) }} and can therefore not be uploaded here! Consider, finding a version
                                        that is smaller in size or alternatively compressing the file before uploading.
                                    </div>
                                    <div v-if="fileUploadPercentCompleted === 0" class="col-md-12">
                                        <b-form inline >
                                            <b-form-select class="form-control-sm ml-0 mr-1 w-50" size="sm"
                                                           v-model="selectedFileSource"
                                                           :options="fileTypeOptions"
                                                           :disabled="uploadSizeFail || uploadTypeFail"
                                            ></b-form-select>
                                            <b-input
                                                id="data-source-date"
                                                v-model="fileDataDate"
                                                type="date"
                                                class="form-control-sm ml-0 mr-1"
                                                placeholder="Valid date (yyyy-mm-dd)"
                                                :disabled="uploadSizeFail || uploadTypeFail"
                                            ></b-input>
                                            <b-button class="form-control-sm ml-0 mr-1 float-right" size="sm" variant="primary" @click="storeUploadFile" :disabled="uploadFileIsDisabled">
                                                <span class="text-nowrap">Upload</span>
                                            </b-button>
                                            <b-button class="form-control-sm ml-0 mr-1 float-right" size="sm" variant="secondary" @click="onFileCancel" :disabled="false">
                                                <span class="text-nowrap">Cancel</span>
                                            </b-button>
                                        </b-form>
                                    </div>
                                    <div v-else class="text-right">
                                        {{ uploadProgressText }} <span v-if="fileUploadPercentCompleted === 100" class="fa fa-check text-success"></span>
                                    </div>
                                </b-card-text>
                            </b-card>
                        </div>
                    </div>
                </div>
                <!-- Comments-->
                <div class="row mt-2 mb-2 mx-1">
                    <div class="col-md-12 w-100">
                        <label for="research-notes" class="font-weight-bolder">
                            Research Notes
                            <span class="fa fa-info-circle px-2"
                                  v-b-popover.hover.righttop="helpTexts.researchNotes"
                                  title="Research Notes"
                            ></span>
                        </label>
                        <div class="col-12 text-info" v-if="showHelpTextsInline"><p>{{ helpTexts.researchNotes }}</p></div>
                        <b-form-textarea
                            id="research-notes"
                            v-model="researchNotes"
                            placeholder="..."
                            rows="3"
                            max-rows="6"
                            :class="researchNotesRemainingChars < 0 ? 'cax-bg-danger' : ''"
                        ></b-form-textarea>
                        <div class="float-right"><span class="mt-0 mb-0 text-info">{{ researchNotesRemainingChars }} characters remaining</span></div>
                    </div>
                </div>
                <!--Confirmation-->
                <div class="row mt-3 mb-2 mx-1">
                    <div class="col-md-9 offset-md-3">
                        <b-form-checkbox
                            id="confirmation"
                            v-model="confirmation"
                            name="confirmation"
                            value="confirmed"
                            unchecked-value="not_confirmed"
                        >
                            I have checked the prices and basic specifications, ABS availablity in particular, of all models and versions in the grid against the latest available source information and confirm that they are up to date.
                        </b-form-checkbox>
                        <span class="float-right text-info mt-1">{{ user.name }} on {{ currentDateTime() }}</span>
                    </div>
                </div>
            </form>                <!-- Footer -->
            <template v-slot:modal-footer="{ok, cancel}">
                <b-button size="sm" variant="secondary" @click="cancel()">
                    Cancel
                </b-button>
                <b-button size="sm"
                          variant="primary"
                          @click="ok()"
                          :disabled="confirmIsDisabled">
                    Confirm
                </b-button>
            </template>

        </b-modal>
    </div>
</template>

<script>
import * as math from "lodash";

export default {
    props: {
        user: {
            type: Object,
            default: {}
        },
        country: {
            type: Object,
            default: {}
        },
        brand: {
            type: Object,
            default: {}
        }
    },

    data () {
        return {
            sourceId: null,
            sourceType: 'DataCheck',
            urls: [],
            url: '',
            selectedUrls: [], // Must be an array reference!
            urlResponse: null,
            urlDisplayLength: 70,
            urlsRetrieved: null,
            fileResponse: null,
            selectedFile: null,
            fileSizeMaximum: 15,
            fileSizePower: 1000,
            fileTypeOptions: [
                { value: null, text: 'Please select a data source type' },
                { value: 'Brochure', text: 'Brochure' },
                { value: 'Image (studio)', text: 'Image (studio)' },
                { value: 'Image (action)', text: 'Image (action)' },
                { value: 'Press Kit', text: 'Press Kit' },
                { value: 'Press Release', text: 'Press Release' },
                { value: 'Price list', text: 'Price list' },
                { value: 'Specification sheet', text: 'Specification sheet' },
                { value: 'Other', text: 'Other' },
            ],
            selectedFileSource: null,
            fileDataDate: null,
            fileUploadPercentCompleted: 0,
            uploadedFiles: null,
            fileNameDisplayLength: 45,
            researchNotes: '',
            researchNotesMaxLength: 1000,
            confirmation: 'not_confirmed',
            csrf: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            showHelpTextsInline: false,
            helpTexts: {
                sourceUrls: 'Provide at least one URL for the data source that you used to perform your data check. If you used a previously added URL, then confirm that link by clicking the checkbox next to it.',
                sourceDataFiles: 'In case you used data sources in file format, then please upload them using the "Add source file" button.',
                researchNotes: 'When you want to make notes for future reference about the current state of affairs for this brand, you can type them in the comments area below.',
            },
        }
    },

    computed: {
        researchNotesRemainingChars: function () {
            return this.researchNotesMaxLength - this.researchNotes.length;
        },
        confirmIsDisabled: function () {
            let urlFail = this.selectedUrls.length < 1;
            let notesFail = this.researchNotesRemainingChars < 0
            let confirmFail = this.confirmation !== 'confirmed';
            return urlFail || notesFail || confirmFail;
        },
        urlIsInvalid: function () {
            let structureFail = !this.validateUrl;
            let hasContent = this.url.length > 10;
            return structureFail && hasContent;
        },
        addUrlIsDisabled: function () {
            let structureFail = !this.validateUrl;
            let minLengthFail = this.url.length < 12;
            return structureFail || minLengthFail;
        },
        urlList: function () {
            return this.urls;
        },
        addSourceFileButtonIsDisabled: function () {
            return !! this.selectedFile;
        },
        maxUploadSize: function () {
            return this.fileSizePower * this.fileSizePower * this.fileSizeMaximum;
        },
        uploadSizeFail: function () {
            return this.selectedFile.size > this.maxUploadSize;
        },
        uploadTypeFail: function () {
            return !this.fileFaIcon(this.selectedFile.type);
        },
        uploadFormIsEnabled: function () {
            return this.selectedFile;
        },
        uploadFileIsDisabled: function () {
            return !this.uploadFormIsEnabled || this.uploadTypeFail || this.uploadSizeFail || !this.selectedFileSource;
        },
        uploadProgressText: function () {
            let text = '';
            let progress = this.fileUploadPercentCompleted;
            if (progress > 0) {
                text = 'File upload progress ' + progress + '%';
            }
            return text;
        },
        validateUrl: function () {
            //
            // Regular Expression for URL validation
            //
            // Author: Diego Perini
            // Created: 2010/12/05
            // Updated: 2018/09/12
            // License: MIT
            //
            // Copyright (c) 2010-2018 Diego Perini (http://www.iport.it)
            //
            const re_weburl = new RegExp(
                "^" +
                // protocol identifier (optional)
                // short syntax // still required
                "(?:(?:(?:https?|ftp):)?\\/\\/)" +
                // user:pass BasicAuth (optional)
                "(?:\\S+(?::\\S*)?@)?" +
                "(?:" +
                // IP address exclusion
                // private & local networks
                "(?!(?:10|127)(?:\\.\\d{1,3}){3})" +
                "(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})" +
                "(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})" +
                // IP address dotted notation octets
                // excludes loopback network 0.0.0.0
                // excludes reserved space >= 224.0.0.0
                // excludes network & broadcast addresses
                // (first & last IP address of each class)
                "(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])" +
                "(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}" +
                "(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))" +
                "|" +
                // host & domain names, may end with dot
                // can be replaced by a shortest alternative
                // (?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+
                "(?:" +
                "(?:" +
                "[a-z0-9\\u00a1-\\uffff]" +
                "[a-z0-9\\u00a1-\\uffff_-]{0,62}" +
                ")?" +
                "[a-z0-9\\u00a1-\\uffff]\\." +
                ")+" +
                // TLD identifier name, may end with dot
                "(?:[a-z\\u00a1-\\uffff]{2,}\\.?)" +
                ")" +
                // port number (optional)
                "(?::\\d{2,5})?" +
                // resource path (optional)
                "(?:[/?#]\\S*)?" +
                "$", "i"
            );
            return this.url.match(re_weburl);
        }
    },

    mounted () {
        this.getUrls();
        this.getFiles();
        // setInterval(() => { this.currentDateTime() }, 1000);
    },

    methods: {
        shortDate: function (dateIn) {
            let dateToConvert = new Date(dateIn);
            let dateSep = '-';
            let dateString =
                dateToConvert.getFullYear() + dateSep +
                ("0" + (dateToConvert.getMonth() + 1)).slice(-2) + dateSep +
                ("0" + dateToConvert.getDate()).slice(-2)
            ;
            return dateString;
        },
        currentDateTime: function () {
            let currentDT = new Date();
            let dateSep = '-';
            let timeSep = ':';
            let dateString =
                currentDT.getFullYear() + dateSep +
                ("0" + (currentDT.getMonth() + 1)).slice(-2) + dateSep +
                ("0" + currentDT.getDate()).slice(-2) + ' at ' +
                ("0" + currentDT.getHours()).slice(-2) + timeSep +
                ("0" + currentDT.getMinutes()).slice(-2)
                // + timeSep + ("0" + currentDT.getSeconds()).slice(-2)
            ;
            return dateString;
        },
        formatUrlText: function (url) {
            let urlText = url;
            let fromPosition = 0;
            let patterns = ['https://www.', 'https://', 'http://', 'http://www.'];
            for (let i = 0; i < patterns.length; i++) {
                let pos = urlText.search(patterns[i]);
                if (pos === 0) {
                    if (patterns[i].length > fromPosition) {
                        fromPosition = patterns[i].length
                    }
                }
            }
            urlText = urlText.substr(fromPosition, urlText.length)
            if (urlText.length > this.urlDisplayLength) {
                urlText = urlText.substr(0, this.urlDisplayLength) + '...'
            }
            return urlText;
        },
        updateUrlList: function (newUrl) {
            this.selectedUrls.push(newUrl.id);
            let urlText = this.formatUrlText(newUrl.url);
            // if (urlText.length > this.urlDisplayLength) {urlText = urlText.substr(0, this.urlDisplayLength)+'...'}
            let listEntry = {text: urlText, value: newUrl.id};
            this.urls.unshift(listEntry);
            this.url = '';
            this.$root.$emit('bv::toggle::collapse', 'add-url')
            return newUrl;
        },
        convertToUrlList: function (urlsJson) {
            return urlsJson.map(item => {
                const container = {};
                container['value'] = item.id;
                container['text'] = this.formatUrlText(item.url);
                return container;
            })
        },
        touchSelectedUrls: function (context) {
            this.selectedUrls.forEach(function (id) {
                context.touchUrl(id);
            })
        },
        archiveSelectedUrls: function (context) {
            this.selectedUrls.forEach(function (id) {
                context.archiveUrl(id);
            })
            this.selectedUrls = [];
        },
        storeUrl: function () {
            axios.post('/api/url', {
                country_id: this.country.id,
                brand_id: this.brand.id,
                url: this.url,
                urlable_id: null,
                urlable_type: 'DataCheck'
            })
                .then(response => {
                    this.urlResponse = response.data;
                    this.updateUrlList(this.urlResponse);
                })
                .catch(error => {
                    // console.log(error);
                })
        },
        getUrls: function (count) {
            let params = '';
            if (count) {
                params = '?count=' + count
            }
            axios.get('/api/urls/' + this.country.id + '/' + this.brand.id + params)
                .then(response => {
                    this.urlsRetrieved = response.data;
                    this.urls = this.convertToUrlList(response.data);
                })
                .catch(error => {
                    // console.log(error);
                })
        },
        touchUrl: function (id) {
            axios.post('/api/url-touch/' + id)
                .then(response => {
                    let status = response.data;
                })
                .catch(error => {
                    // console.log(error);
                })
        },
        archiveUrl: function (id) {
            axios.post('/api/url-archive/' + id)
                .then(response => {
                    let status = response.data;
                })
                .catch(error => {
                    // console.log(error);
                })
        },
        onArchiveUrl: function () {
            let urls = this.urls;
            let selected = this.selectedUrls;
            for (let i = 0; i < selected.length; i++) {
                let targetId = selected[i];
                let found = urls.findIndex(element => element['value'] === targetId);
                if (found || found === 0) {
                    /** Remove URL form list array */
                    urls.splice(found, 1);
                }
            }
            this.archiveSelectedUrls(this);
        },
        onOk: function() {
            this.touchSelectedUrls(this);
            this.$refs.dataCheckForm.submit();
        },
        onCancel: function() {

        },
        onFileSelected(event) {
            // console.log(event);
            this.selectedFile = event.target.files[0];
            // console.log(this.selectedFile);
        },
        storeUploadFile() {
            const formData = new FormData();
            formData.append('file', this.selectedFile, this.selectedFile.name);
            formData.append('file_name', this.selectedFile.name);
            formData.append('file_mime_type', this.selectedFile.type);
            formData.append('uploadedFileable_id', this.sourceId);
            formData.append('uploadedFileable_type', this.sourceType);
            formData.append('country_id', this.country.id);
            formData.append('brand_id', this.brand.id);
            formData.append('file_type', this.selectedFileSource);
            formData.append('data_date', this.fileDataDate);
            axios.post(
                '/api/file',
                formData,
                {
                    onUploadProgress: uploadEvent => {
                        this.fileUploadPercentCompleted = Math.round(uploadEvent.loaded / uploadEvent.total * 100)
                    }
                }
            )
                .then(response => {
                    // console.log(response.data);
                    this.onFileSuccess();
                    // this.fileResponse = response.data;
                })
                .catch(error => {
                    // console.log(error);
                })
        },
        onFileSuccess: function () {
            this.getFiles();
            this.selectedFile = null;
            this.selectedFileSource = null;
            this.fileDataDate = null;
            this.fileUploadPercentCompleted = 0;
        },
        onFileCancel: function () {
            this.selectedFile = null;
        },
        archiveUploadFile: function (id) {
            axios.post('/api/file-archive/' + id)
                .then(response => {
                    let status = response.data;
                    this.getFiles();
                })
                .catch(error => {
                    // console.log(error);
                })
        },
         getFiles: function (count) {
            let params = '';
            if (count) {
                params = '?count=' + count
            }
            axios.get('/api/files/' + this.country.id + '/' + this.brand.id + params)
                .then(response => {
                    this.uploadedFiles = response.data;
                })
                .catch(error => {
                    // console.log(error);
                })
        },
        formatFileNameText: function (fileName) {
            let text = fileName;
            let fromPosition = 0;
            text = text.substr(fromPosition, text.length)
            if (text.length > this.fileNameDisplayLength) {
                text = text.substr(0, this.fileNameDisplayLength) + '...'
            }
            return text;
        },
        humanFileSize: function (rawSize) {
            let huSize = rawSize;
            let huUnit = 'b';
            let power = this.fileSizePower;
            if (huSize > (power * power)) {
                huSize = huSize / (power * power);
                huUnit = 'Mb'
            } else if (huSize > power) {
                huSize = huSize / power;
                huUnit = 'Kb'
            }
            return math.round(huSize * 10) / 10 + ' ' + huUnit;
        },
        filePopOverText: function (file) {
            let text = 'Name:' + file.name;
            text = text + ' ' + 'Size:'+ this.humanFileSize(file.media[0].size);
            text = text + ' ' + 'Mime-type:'+ file.media[0].mime_type;
            text = text + ' ' + 'Created:'+ file.created_at;
            return text;
        },
        fileFaIcon: function (fileType) {
            let iconClass = '';
            let fileInterjection = 'file-';
            let allowedFileTypes = this.allowedFileTypes();
            if (!allowedFileTypes) {return ''}
            Object.keys(allowedFileTypes).forEach((id, index) => {
                let allowedTypes = allowedFileTypes[id];
                if (allowedTypes.includes(fileType)) {
                    if (id === 'file') {fileInterjection = ''}
                    iconClass = 'fa fa-' + fileInterjection + id;
                }
            });
            return iconClass;
        },
        allowedFileTypes: function () {
            return {
                image: [
                    'image/bmp',
                    'image/gif',
                    'image/jpeg',
                    'image/png',
                    'image/webp',
                    'image/svg+xml',
                ],
                pdf: [
                    'application/pdf',
                ],
                text: [
                    'text/plain',
                ],
                archive: [
                    'application/zip',
                    'application/gzip',
                    'application/vnd.rar',
                    'application/x-tar',
                ],
                excel: [
                    'application/vnd.ms-excel',
                    'application/vnd.oasis.opendocument.spreadsheet',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'text/csv',
                ],
                word: [
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/vnd.oasis.opendocument.text',
                    'application/rtf',
                ],
                powerpoint: [
                    'application/vnd.ms-powerpoint',
                    'application/vnd.oasis.opendocument.presentation',
                    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                ],
                file: [
                    'application/epub+zip',
                ],
            }
        },
    },
}
</script>
